import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import toastr from 'toastr';
import { MFChannel } from '../../../Components/SDK';
import { getAppOnlineStatus, setUserInfoArr } from '../../../slice';
import { NO_INTERNET } from '../constants';
import logger from '../../../Helpers/logger';

export function usePaginatedUserList({ channelId, initialSearch = '' }) {
    const dispatch = useDispatch();
    const [userList, setUserList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [searchText, setSearchText] = useState(initialSearch);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const debounceTimer = useRef(null);

    const fetchUsers = useCallback(
        async (page, search) => {
            try {
                if (!getAppOnlineStatus()) {
                    toastr.error(NO_INTERNET);
                    return [];
                }
                setLoading(true);
                setError(null);

                const res = await MFChannel.fetchChannelNonMembers({
                    channelId,
                    page,
                    size: 20,
                    search,
                });
                const users = res?.data?.userList || [];
                const totalPages = res?.data?.totalPages || 1;

                setUserList(prev => {
                    const combined = page === 1 ? users : [...prev, ...users];

                    const seen = new Set();
                    const unique = [];
                    const duplicates = [];

                    for (const u of combined) {
                        if (seen.has(u.userId)) {
                            duplicates.push(u);
                        } else {
                            seen.add(u.userId);
                            unique.push(u);
                        }
                    }

                    if (duplicates.length > 0) {
                        logger.warn('Duplicate users detected:', duplicates);
                    }

                    return unique; // ✅ return unique list instead of combined
                });

                dispatch(setUserInfoArr(users));
                setHasMore(page < totalPages);
            } catch (err) {
                logger.error('Error fetching user list:', err);
                setError(err);
                setHasMore(false);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 1000);
            }
        },
        [channelId, dispatch],
    );

    // Initial fetch
    useEffect(() => {
        fetchUsers(pageNumber, searchText);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced search
    const updateSearchText = useCallback(
        newSearch => {
            if (!getAppOnlineStatus()) return;
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                setPageNumber(1);
                setSearchText(newSearch);
                fetchUsers(1, newSearch);
            }, 300);
        },
        [fetchUsers],
    );

    const loadMore = () => {
        if (hasMore && !loading) {
            setPageNumber(prev => prev + 1);
            fetchUsers(pageNumber + 1, searchText);
        }
    };

    const reset = () => {
        setPageNumber(1);
        setHasMore(true);
        fetchUsers(1, searchText);
    };

    return {
        userList,
        loading,
        error,
        hasMore,
        loadMore,
        setSearchText: updateSearchText,
        reset,
    };
}
